<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import api from '~/api';
import axios from 'axios';
import CropImageDialog from '~/components/editor/CropImageDialog.vue';
import MaterialSelector from '~/components/MaterialSelector.vue';
import type { MaterialWithStatus } from '~/types';

type Tool = 'face-swap' | 'sex-pose' | 'ai-video' | 'seedance';

const props = defineProps<{
  tool: Tool;
  result: { url: string; type: 'image' | 'video' } | null;
  /** 当前是否生成中（提交/轮询）。生成中时上传区改为「去 Creations 等 / 重新上传」 */
  generating?: boolean;
}>();
const emit = defineEmits<{
  (e: 'upload', payload: { url: string; materialId: string; ratio?: string }): void;
  (e: 'reset'): void;
  (e: 'uploading', value: boolean): void;
}>();

// 与 CropImageDialog / EditorPanelSexPose 的比例列表保持一致
const STD_RATIOS: [string, number][] = [
  ['1:1', 1], ['3:4', 3 / 4], ['4:3', 4 / 3],
  ['9:16', 9 / 16], ['16:9', 16 / 9], ['2:3', 2 / 3], ['3:2', 3 / 2],
];
function nearestRatio(w: number, h: number): string {
  if (!w || !h) return '';
  const r = w / h;
  let best = STD_RATIOS[0];
  let diff = Infinity;
  for (const s of STD_RATIOS) {
    const d = Math.abs(s[1] - r);
    if (d < diff) { diff = d; best = s; }
  }
  return best[0];
}

const authStore = useAuthStore();
const router = useRouter();
const localePath = useLocalePath();
const { t } = useI18n();

const isUploading = ref(false);
const dragOver = ref(false);
const uploadedUrl = ref('');
const uploadedMaterialId = ref('');
const errorMsg = ref('');
interface RecentMat {
  url: string;
  material_id: string;
  cover_url?: string;
  convert_url?: string;
  format?: string;
}
const recentMaterials = ref<RecentMat[]>([]);

const VIDEO_EXT = ['mp4', 'mov', 'mpeg', 'webm', '3gp', 'm4v', 'avi', 'mkv'];
function isVideoMat(m: RecentMat): boolean {
  if (m.format) return VIDEO_EXT.includes(m.format.toLowerCase());
  const ext = m.url?.split('?')[0].split('.').pop()?.toLowerCase();
  return !!ext && VIDEO_EXT.includes(ext);
}

// 仅 face-swap 支持视频；sex-pose / ai-video 只接受图片
const acceptAttr = computed(() => props.tool === 'face-swap' ? 'image/*,video/*' : 'image/*');
const formatsLabel = computed(() => props.tool === 'face-swap'
  ? 'JPG, PNG, WEBP, MP4'
  : 'JPG, PNG, WEBP');

const cropOpen = ref(false);
const cropFile = ref<File | null>(null);
const cropRatio = ref('');
const materialSelectorOpen = ref(false);

// 结果下载走同源代理（跨域 a[download] 不生效，会变成跳转预览）
const downloadHref = computed(() =>
  props.result ? `/api/download?url=${encodeURIComponent(props.result.url)}` : '#',
);

async function loadMaterials() {
  if (!authStore.isLogin) return;
  try {
    // sex-pose / ai-video 只收图，多取一些再过滤掉视频，保证仍能凑够缩略图
    const res = await api.getMyUploads('all', 1, props.tool === 'face-swap' ? 5 : 15);
    const mapped: RecentMat[] = (res.lists || []).map((m: any) => ({
      url: m.url,
      material_id: m.material_id,
      cover_url: m.cover_url,
      convert_url: m.convert_url,
      format: m.format,
    }));
    recentMaterials.value = (
      props.tool === 'face-swap' ? mapped : mapped.filter((m) => !isVideoMat(m))
    ).slice(0, 5);
  } catch {/* ignore */}
}
onMounted(loadMaterials);
// 切换工具时（face-swap↔sex-pose↔ai-video）重载，过滤规则随之变化
watch(() => props.tool, () => loadMaterials());

async function doUpload(file: File, ratio?: string) {
  if (!authStore.isLogin) {
    router.push(localePath('/login/google?redirect=/editor'));
    return;
  }
  if (file.size > 50 * 1024 * 1024) {
    errorMsg.value = t('editor.fileTooLarge');
    return;
  }
  errorMsg.value = '';
  isUploading.value = true;
  emit('uploading', true);
  try {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const sign = await api.getSignUrl(ext);
    await axios.put(sign.sign_url, file, {
      headers: { 'Content-Type': file.type || getMimeType(file.name) },
    });
    const url = (sign as any).file_url || `https://cdn.treekee.com/bestfaceswap/uploads/${sign.material_id}.${ext}`;
    await api.confirmUpload({
      material_id: sign.material_id,
      url,
      format: ext,
      size: file.size,
    });
    uploadedUrl.value = url;
    uploadedMaterialId.value = sign.material_id;
    emit('upload', { url, materialId: sign.material_id, ratio });
    await loadMaterials();
  } catch (e: any) {
    errorMsg.value = e?.message || t('editor.uploadFailed');
  } finally {
    isUploading.value = false;
    emit('uploading', false);
  }
}

function handlePickedFile(file: File) {
  if (!authStore.isLogin) {
    router.push(localePath('/login/google?redirect=/editor'));
    return;
  }
  // 非 face-swap 工具不允许视频
  if (file.type.startsWith('video/') && props.tool !== 'face-swap') {
    errorMsg.value = t('editor.videoOnlyFaceSwap');
    return;
  }
  if (file.type.startsWith('image/')) {
    cropFile.value = file;
    cropOpen.value = true;
  } else {
    void doUpload(file);
  }
}

function onFile(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) handlePickedFile(file);
  input.value = '';
}

function onDrop(event: DragEvent) {
  event.preventDefault();
  dragOver.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) handlePickedFile(file);
}

function onCropResult(file: File | null) {
  const ratio = cropRatio.value || undefined;
  cropFile.value = null;
  if (file) void doUpload(file, ratio);
}

function pickRecent(m: { url: string; material_id: string }) {
  uploadedUrl.value = m.url;
  uploadedMaterialId.value = m.material_id;
  // 历史素材没有比例信息，读真实尺寸算最接近的标准比例再上抛
  const img = new Image();
  img.onload = () =>
    emit('upload', {
      url: m.url,
      materialId: m.material_id,
      ratio: nearestRatio(img.naturalWidth, img.naturalHeight) || undefined,
    });
  img.onerror = () => emit('upload', { url: m.url, materialId: m.material_id });
  img.src = m.url;
}

// 历史素材弹窗（与 /face-swap 的 MaterialsHistory 用同一个 MaterialSelector）。
// 之前这里是 <NuxtLink to="/my/uploads">，会直接跳页 —— 不对，应弹窗选择
function openMaterialSelector() {
  if (!authStore.isLogin) {
    router.push(localePath('/login/google?redirect=/editor'));
    return;
  }
  materialSelectorOpen.value = true;
}

function onMaterialSelected(m: MaterialWithStatus) {
  if (m?.url && m?.material_id) pickRecent({ url: m.url, material_id: m.material_id });
}

// 生成中：用户不必干等，可去 Creations 看进度，或整页重来发新任务
// （与 /face-swap 生成中的两个按钮行为一致）
function goToCreations() {
  router.push(localePath('/my/creations'));
}
function restartUpload() {
  router.go(0);
}
</script>

<template>
  <div class="zone" :class="{ 'drag-over': dragOver }">
    <div
      class="dropper"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop="onDrop"
    >
      <!-- 结果展示(优先于上传/裁剪) -->
      <div v-if="props.result" class="result-area">
        <img v-if="props.result.type === 'image'" :src="props.result.url" alt="result" referrerpolicy="no-referrer" />
        <video v-else :src="props.result.url" controls autoplay loop muted referrerpolicy="no-referrer" />

        <div class="result-toolbar">
          <a class="tool-btn" :href="downloadHref" download :title="t('editor.download')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </a>
          <button class="tool-btn" :title="t('editor.recreate')" @click="emit('reset')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </button>
        </div>
      </div>

      <!-- 生成中：不必干等，去 Creations 看进度或整页重来发新任务 -->
      <div v-else-if="props.generating" class="gen-default">
        <span class="gen-spinner" />
        <h3 class="grad">{{ t('editor.generating') }}</h3>
        <div class="gen-actions">
          <button type="button" class="gen-wait" @click="goToCreations">
            {{ t('editor.waitInCreations') }}
          </button>
          <button type="button" class="gen-new" @click="restartUpload">
            {{ t('editor.uploadNew') }}
          </button>
        </div>
      </div>

      <!-- 默认 drag-drop(始终展示,即便已选素材) -->
      <div v-else class="drop-default">
        <h3 class="grad">{{ t('editor.dropTitle') }}</h3>
        <p class="sub">{{ t('editor.dropSub') }}</p>
        <label class="upload-btn">
          <input type="file" :accept="acceptAttr" class="hidden" :disabled="isUploading" @change="onFile" />
          <span v-if="isUploading">{{ t('editor.uploading') }}</span>
          <span v-else>{{ t('editor.uploadPhoto') }}</span>
        </label>
        <p class="formats">{{ t('editor.supportedFormats', { formats: formatsLabel }) }}</p>
      </div>

      <p v-if="errorMsg" class="err">{{ errorMsg }}</p>
    </div>

    <!-- 历史素材 -->
    <div class="materials">
      <div class="materials-head">
        <span class="materials-title">
          {{ uploadedUrl ? t('editor.materialSelected') : (recentMaterials.length ? t('editor.recentUploads') : t('editor.noMaterial')) }}
        </span>
        <button type="button" class="more" @click="openMaterialSelector">
          {{ t('editor.viewMore') }} <span>›</span>
        </button>
      </div>
      <div v-if="recentMaterials.length" class="thumbs">
        <button
          v-for="m in recentMaterials" :key="m.url"
          class="thumb"
          :class="{ selected: uploadedUrl === m.url }"
          @click="pickRecent(m)"
        >
          <img
            v-if="m.cover_url || !isVideoMat(m)"
            :src="m.cover_url || m.convert_url || m.url"
            alt=""
            loading="lazy"
          />
          <video
            v-else
            :src="`${m.convert_url || m.url}#t=0.1`"
            muted
            playsinline
            preload="metadata"
          />
          <span v-if="isVideoMat(m)" class="vid-badge" aria-hidden="true">▶</span>
          <span v-if="uploadedUrl === m.url" class="check">✓</span>
        </button>
      </div>
    </div>

    <CropImageDialog
      v-model="cropOpen"
      :image="cropFile"
      v-model:selected-ratio="cropRatio"
      @crop="onCropResult"
    />

    <MaterialSelector
      v-model="materialSelectorOpen"
      :image-only="props.tool !== 'face-swap'"
      @select="onMaterialSelected"
    />
  </div>
</template>

<style scoped lang="scss">
.zone {
  @apply rounded-2xl border-2 border-dashed border-amber-500/40 bg-neutral-950 p-6 min-h-[600px] flex flex-col gap-4;
  &.drag-over { @apply border-amber-500 bg-amber-500/5; }
}
.dropper { @apply flex-1 flex flex-col items-center justify-center; }

.drop-default { @apply text-center space-y-3; }
.grad {
  @apply text-2xl md:text-3xl font-bold;
  background: linear-gradient(135deg, #f97316 0%, #fbbf24 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.sub { @apply text-white/70; }
.upload-btn {
  @apply inline-flex items-center justify-center rounded-full px-10 py-4 mt-4 font-semibold text-white cursor-pointer transition-all;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
  box-shadow: 0 8px 24px -8px rgba(249,115,22,0.5);
  &:hover { transform: scale(1.03); }
}
.formats { @apply mt-3 text-xs text-white/40; }

.gen-default { @apply text-center flex flex-col items-center gap-4; }
.gen-spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid rgba(240, 105, 6, 0.25);
  border-top-color: #f06906;
  border-radius: 9999px;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.gen-actions { @apply flex flex-col gap-3 w-full max-w-xs mt-1; }
.gen-wait {
  @apply w-full rounded-full py-3 font-semibold transition whitespace-nowrap;
  background: rgba(240, 105, 6, 0.18);
  color: #f06906;
  &:hover { background: rgba(240, 105, 6, 0.28); }
}
.gen-new {
  @apply w-full rounded-full py-3 font-semibold text-black transition whitespace-nowrap;
  background: #f06906;
  &:hover { transform: scale(1.03); }
}

.uploaded-preview {
  @apply relative w-full max-w-md;
  img { @apply w-full max-h-[400px] object-contain rounded-xl; }
}
.clear-btn {
  @apply absolute right-2 top-2 size-8 rounded-full bg-black/70 text-white text-xl hover:bg-black/90;
}

.result-area {
  @apply relative w-full max-w-2xl;
  img, video { @apply w-full max-h-[440px] object-contain rounded-xl; }
}
.result-toolbar {
  @apply absolute right-3 top-3 flex gap-2 rounded-full px-2 py-2 backdrop-blur-md;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.tool-btn {
  @apply inline-flex h-9 w-9 items-center justify-center rounded-full text-white/85
    hover:text-white hover:bg-white/10 cursor-pointer;
  svg { width: 1.05rem; height: 1.05rem; }
}

.err { @apply text-sm text-red-400 mt-2; }

.materials { @apply mt-2 space-y-3; }
.materials-head { @apply flex items-center justify-between; }
.materials-title { @apply text-amber-400 text-sm font-medium; }
.more {
  @apply inline-flex items-center gap-1 rounded-full border border-neutral-700 px-3 py-1.5 text-sm text-white/70 hover:bg-neutral-800;
  span { @apply text-base leading-none; }
}
.thumbs { @apply flex gap-2 overflow-x-auto pb-2; }
.thumb {
  @apply relative flex-shrink-0 size-24 rounded-xl overflow-hidden border-2 border-transparent hover:border-amber-500;
  img, video { @apply size-full object-cover; }
  .vid-badge {
    @apply absolute inset-0 m-auto size-7 rounded-full bg-black/55 text-white
      flex items-center justify-center text-[10px] pointer-events-none;
  }
  &.selected { @apply border-rose-500; }
  .check {
    @apply absolute right-1 bottom-1 size-5 rounded-full bg-rose-500 text-white text-xs flex items-center justify-center font-bold;
  }
}
</style>
